import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
// import Sidebar from './scenes/global/Sidebar';
import { CircularProgress, CssBaseline, Drawer, Stack, ThemeProvider, Typography, useMediaQuery } from '@mui/material';
import { ColorModeContext, useMode } from './theme';
import { useDispatch } from 'react-redux';
import { user } from './context/permissionSlice';
import { settings } from './context/settingsSlice';
import moment from 'moment';
import { HttpStatusCode } from 'axios';
import { t } from 'i18next';
import { GetProfileInfoApi } from './utils/Api/Settings';
import { useSocket } from './context/SocketContext';
import { useData } from './context/DataContext';
import RadixToastProvider from './components/radix/RadixToastProvider';
import { CustomerProvider } from './context/customer/CustomerContext';
import Sidebar from './components/Sidebar';
import SidebarIcon from '@/assets/Marketing/SidebarIcon.svg';
import { MdClose } from 'react-icons/md';
import { LayoutContext } from './context/LayoutContext';
import { ToastContainer } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Toaster } from 'sonner';
import { useDataProcessingAgreements } from './hooks/useDataProcessingAgreements';

function App() {
    const [theme, colorMode] = useMode();
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    // If ?aid= is present, block the outlet until getdata() writes the token; otherwise
    // ProtectedRoute renders first, redirects to /, and the handoff query string is lost.
    const [redirctLoading, setRedirctLoading] = useState(() => {
        if (typeof window === 'undefined') return false;
        return Boolean(new URLSearchParams(window.location.search).get('aid'));
    });
    const auth2 = localStorage.getItem('auth_token');
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [showMenuIcon, setShowMenuIcon] = useState(false);
    const { storeSettings, refreshSettings } = useData();

    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const sidebarRef = useRef(null);
    const { socketIsOn: socket } = useSocket();
    const [isCollapse, setCollapse] = useState(false);
    const setting = useSelector((state) => state?.settings?.data);
    const isWebView = setting?.from_app;

    useEffect(() => {
        setCollapse(true);
    }, [isMobile]);

    const publicRoutes = ['/', '/employee-setup', '/clinic-setup', '/booking/'];
    const isPublicRoute = publicRoutes.includes(location.pathname);

    const dataProcessingAgreementModals = useDataProcessingAgreements({
        enabled: Boolean(auth2) && !redirctLoading && !isPublicRoute ? true : false,
    });

    async function getdata() {
        try {
            setRedirctLoading(true);
            const params = new URLSearchParams(window.location.search);
            const auth_token = params.get('aid');
            const employee_role = params.get('er');
            const employee_id = params.get('eid');
            const isAppFromURL = params.get('from_app') === 'true';
            const isDoctor = employee_role === 'DOCTOR';

            if (auth_token) {
                // Clear previous session so dashboard handoff starts with a fresh token login
                localStorage.clear();
                localStorage.setItem('auth_token', auth_token);
                localStorage.setItem('employee_role', employee_role);
                localStorage.setItem('employee_id', employee_id);
                localStorage.setItem('image_url', process.env.REACT_APP_IMG_URL);
                // Defer API call to next tick so the request interceptor reads the token from localStorage
                await new Promise((resolve) => setTimeout(resolve, 0));
                const response = await GetProfileInfoApi();
                if (response?.status === HttpStatusCode.Ok) {
                    const employess = response?.data?.data?.employees;
                    const posSettings = response?.data?.data?.settings?.find(
                        (setting) =>
                            setting.settingCategory === 'pos_settings' &&
                            setting.settingName === 'pos_general_settings',
                    );
                    const parsedPosSettings = posSettings ? JSON.parse(posSettings?.value) : null;
                    const employeePOSPermissions = parsedPosSettings?.employeePermissions?.[employee_id] || null;
                    localStorage.setItem('employees', JSON.stringify(employess));
                    const selectedEmp = employess && employess.find((emp) => emp.id == employee_id);
                    dispatch(user({ ...selectedEmp, pos_settings: employeePOSPermissions }));
                    dispatch(settings({ from_dashboard: true, from_app: isAppFromURL, isDoctor }));
                    localStorage.setItem('from_dashboard', true);
                    await refreshSettings();

                    if (isAppFromURL) {
                        navigate('pos/cashdrawer');
                    } else if (isDoctor) {
                        navigate('/customers', { replace: true });
                    } else {
                        // LoginForm skips /calendar when ?aid= is present; we must enter the app here
                        // for handoffs on public entry URLs (e.g. /?aid=...).
                        const path = window.location.pathname;
                        const isPublicHandoffEntry =
                            path === '/' ||
                            path === '/employee-setup' ||
                            path === '/clinic-setup' ||
                            path.startsWith('/booking/');
                        if (isPublicHandoffEntry) {
                            navigate('/calendar', { replace: true });
                        }
                    }
                }
            } else if (!auth_token && !auth2 && !isPublicRoute) {
                navigate('/');
            }
        } catch (error) {
            if (!isPublicRoute) {
                navigate('/');
            }
        } finally {
            setRedirctLoading(false);
        }
    }

    useEffect(() => {
        getdata();
        const metaTag = document.querySelector('meta[name=viewport]');
        if (!metaTag) return;

        const isIOSRequestingDesktopSite = /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;

        if (isIOSRequestingDesktopSite) {
            // ✅ Only if iOS is pretending to be desktop
            metaTag.setAttribute('content', 'user-scalable=no');
        } else {
            metaTag.setAttribute(
                'content',
                'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
            );
        }
    }, []);

    moment.locale('da');
    moment.updateLocale('da', { week: { dow: 1 } });

    useEffect(() => {
        if (!socket) return;

        socket.on('outlet_setting_updated', refreshSettings);
        socket.on('outlet_setting_updated_web_admin', refreshSettings);

        return () => {
            socket.off('outlet_setting_updated', refreshSettings);
            socket.off('outlet_setting_updated_web_admin', refreshSettings);
        };
    }, [socket]);

    useEffect(() => {
        const pathname = location?.pathname ?? '';
        const isSettingsRoute = pathname === '/settings' || pathname.startsWith('/settings/');
        const isPOSSettingsRoute = pathname === '/pos/settings' || pathname.startsWith('/pos/settings/');
        const isCalendarRoute = pathname === '/calendar';
        if (!isSettingsRoute && !isCalendarRoute && !isPOSSettingsRoute) return;
        const timer = setTimeout(() => {
            refreshSettings();
        }, 100);
        return () => clearTimeout(timer);
    }, [location, refreshSettings]);

    useEffect(() => {
        if (isMobile) {
            setIsSidebarOpen(false);
        } else {
            setIsSidebarOpen(true);
        }
    }, [isMobile]);

    useEffect(() => {
        let timer;

        if (isMobile && !isSidebarOpen) {
            timer = setTimeout(() => {
                setShowMenuIcon(true);
            }, 300); // delay in ms
        } else {
            setShowMenuIcon(false);
        }

        return () => clearTimeout(timer);
    }, [isMobile, isSidebarOpen]);

    if (redirctLoading)
        return (
            <Stack
                sx={{
                    position: 'absolute',
                    zIndex: 110,
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                }}
            >
                <CircularProgress size="2.5rem" sx={{ color: '#6f6f6f' }} />
                <Typography variant="body1" sx={{ color: '#6f6f6f', mt: 2 }}>
                    {t('Common.Redirecting')}...
                </Typography>
            </Stack>
        );

    return (
        <ColorModeContext.Provider value={colorMode}>
            <RadixToastProvider>
                <ToastContainer />
                <Toaster position="top-right" richColors closeButton />
                {dataProcessingAgreementModals}
                <ThemeProvider theme={theme}>
                    <CustomerProvider>
                        <CssBaseline />

                        {showMenuIcon && !isWebView && (
                            <img
                                src={SidebarIcon}
                                alt="sidebar_icn"
                                className={`${location?.pathname === '/' || location?.pathname === '/employee-setup' || location?.pathname === '/clinic-setup' || location?.pathname?.startsWith('/booking/') ? 'hidden' : 'fixed'} px-2 py-2.5 cursor-pointer ${location?.pathname === '/calendar' ? 'bg-transparent' : 'bg-white'}`}
                                onClick={() => {
                                    setCollapse(true);
                                    setIsSidebarOpen(true);
                                }}
                                style={{ zIndex: 999 }}
                            />
                        )}

                        <div className="app bg-white">
                            <div className="app-layout">
                                {/* DESKTOP SIDEBAR */}
                                {!isMobile &&
                                    location.pathname !== '/' &&
                                    location.pathname !== '/employee-setup' &&
                                    location.pathname !== '/clinic-setup' &&
                                    !location.pathname.startsWith('/booking/') &&
                                    !isWebView && (
                                        <div className="sidebar-wrapper" style={{ width: isCollapse ? 60 : 170 }}>
                                            <Sidebar
                                                ref={sidebarRef}
                                                storeSettings={storeSettings}
                                                isCollapse={isCollapse}
                                                setCollapse={setCollapse}
                                                isMobile={isMobile}
                                            />
                                        </div>
                                    )}

                                {/* CONTENT */}
                                <LayoutContext.Provider value={{ isCollapse, isMobile }}>
                                    <div className="content-wrapper">
                                        <Outlet />
                                    </div>
                                </LayoutContext.Provider>
                            </div>

                            {/* MOBILE DRAWER (UNCHANGED) */}
                            {isMobile &&
                                !isWebView &&
                                location.pathname !== '/employee-setup' &&
                                location.pathname !== '/tup' && (
                                    <Drawer
                                        open={isSidebarOpen}
                                        onClose={() => {
                                            setIsSidebarOpen(false);
                                            setCollapse(false);
                                        }}
                                        PaperProps={{
                                            sx: {
                                                backgroundColor: 'transparent',
                                                width: '100%',
                                                height: '100vh',
                                                position: 'fixed',
                                                zIndex: 1000,
                                            },
                                        }}
                                    >
                                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                            <Sidebar
                                                storeSettings={storeSettings}
                                                isCollapse={isCollapse}
                                                setCollapse={setCollapse}
                                                isMobile={isMobile}
                                                onClose={() => setIsSidebarOpen(false)}
                                            />

                                            <MdClose
                                                style={{
                                                    color: '#fff',
                                                    fontSize: 40,
                                                    background: '#010101',
                                                    padding: 10,
                                                    borderRadius: '50%',
                                                    cursor: 'pointer',
                                                    position: 'absolute',
                                                    top: '50%',
                                                    right: '20%',
                                                    transform: 'translateY(-50%)',
                                                }}
                                                onClick={() => {
                                                    setIsSidebarOpen(false);
                                                    setCollapse(false);
                                                }}
                                            />
                                        </div>
                                    </Drawer>
                                )}
                        </div>
                    </CustomerProvider>
                </ThemeProvider>
            </RadixToastProvider>
        </ColorModeContext.Provider>
    );
}

export default App;
